import type L from 'leaflet'
import { centroid } from '@turf/centroid'
import * as turf from '@turf/helpers'
import { area } from '@turf/area'
import type { Place, Station } from '@repo/types'

export interface LatLng {
  lat: number
  lng: number
}

// https://maps.gsi.go.jp/development/ichiran.html
export const gsiBaseTileLayer = {
  url: 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png',
  attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener noreferrer">国土地理院</a>',
  minZoom: 5,
  maxZoom: 18,
}

// OSMFJ (OpenStreetMap Foundation Japan) raster tiles: OSM data with localized labels
// ("maptiler-basic-ja" / "maptiler-basic-en" are OSMFJ's own style names) at every zoom level.
// https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver
export const osmBaseTileLayer = {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
  minZoom: 0,
  maxZoom: 20,
}

const OSMFJ_TILE_URL = 'https://tile.openstreetmap.jp/styles/{style}/{z}/{x}/{y}{r}.png'

/**
 * Tile URL for the OSM base layer, labeled in the given locale (Japanese for `ja`, English otherwise).
 *
 * Tiles go through the gateway's caching proxy (`/v1/tiles/{style}/...` on PUBLIC_ENV__API_ORIGIN), so every
 * app/user shares one cache and OSMFJ only sees cache misses. `PUBLIC_ENV__OSM_TILE_URL` overrides the
 * template (`{style}` is replaced by the style name), e.g. to hit OSMFJ directly when no gateway is running.
 */
export function buildOsmTileUrl(locale: string): string {
  const style = locale === 'ja' ? 'maptiler-basic-ja' : 'maptiler-basic-en'
  const apiOrigin = import.meta.env.PUBLIC_ENV__API_ORIGIN as string | undefined
  const template = (import.meta.env.PUBLIC_ENV__OSM_TILE_URL as string | undefined)
    || (apiOrigin ? `${apiOrigin}/v1/tiles/{style}/{z}/{x}/{y}{r}` : OSMFJ_TILE_URL)
  return template.replace('{style}', style)
}

export function serializeMapParams(map: L.Map | null) {
  if (!map) return null
  const center = map.getCenter()
  return `@${center.lat},${center.lng},${map.getZoom()}z`
}

function isStation(p: Place): p is Station {
  return p.level === 'station'
}

import type { Position, MultiLineString, Polygon, MultiPolygon } from 'geojson'

export function getInitialCenter(place: Place | null): LatLng | undefined {
  if (!place) return undefined;

  // Station point
  if (isStation(place)) {
    const coords = place.geometry.coordinates.flat();
    const sum = coords.reduce((a, b) => ({ lat: a.lat + b[1], lng: a.lng + b[0] }), { lat: 0, lng: 0 });
    return { lat: sum.lat / coords.length, lng: sum.lng / coords.length };
  }

  const geom = place.geometry satisfies Polygon | MultiPolygon | MultiLineString

  if (geom.type.includes('Polygon')) {
    // Polygon centroid
    const polygons = geom.type === 'MultiPolygon' ? geom.coordinates.map(p => p[0]) : geom.coordinates;
    const mainPolygon = [...polygons].sort((a, b) => area(turf.polygon([b])) - area(turf.polygon([a]))).shift();
    if (mainPolygon) {
      const c = centroid(turf.polygon([mainPolygon]));
      return { lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0] };
    }
  } else {
    // LineString centroid
    const c = centroid(turf.multiLineString(geom.coordinates as Position[][]));
    return { lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0] };
  }

  return undefined;
}
